import {
  BookOpenText,
  CalendarDays,
  ChartLine,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Droplets,
  Gauge,
  HeartPulse,
  Home,
  LifeBuoy,
  LogOut,
  Menu,
  MonitorDown,
  CookingPot,
  NotebookTabs,
  PackageOpen,
  Settings,
  ShoppingBasket,
  Sparkles,
  UserRound,
  WalletCards,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { FriendAccessStatus } from "../access/friendAccess";
import { PlanStatusBadge } from "../access/PlanAccess";
import { supabase } from "../supabase";
import { LegalFooter } from "./LegalFooter";
import { AppLink, navigate } from "./router";
import { ShareButton } from "./ShareButton";

type NavItem = readonly [href: string, label: string, Icon: LucideIcon];

const primary: readonly NavItem[] = [
  ["/dashboard", "Dashboard", Home],
  ["/generator", "Generator", Sparkles],
  ["/calendar", "Calendar", CalendarDays],
  ["/grocery-lists", "Grocery", ShoppingBasket],
  ["/saved-meals", "Saved Meals", NotebookTabs],
  ["/pantry", "Pantry", PackageOpen],
  ["/meal-builder", "Builder", CookingPot],
  ["/profile", "Profile", UserRound],
] as const;

const tracking: readonly NavItem[] = [
  ["/health-tracking", "Health", HeartPulse],
  ["/hydration", "Hydration", Droplets],
  ["/health-measurements", "Measurements", Gauge],
  ["/glucose-trends", "Glucose", ChartLine],
] as const;

const resources: readonly NavItem[] = [
  ["/guide", "App guide", CircleHelp],
  ["/why-precise-plate", "Why Precise Plate", BookOpenText],
  ["/install", "Install app", MonitorDown],
] as const;

const settingsLink: NavItem = ["/settings", "Settings / Billing", Settings];
const supportLink: NavItem = ["/support", "Support", LifeBuoy];
const administrationLink: NavItem = ["/admin/finance", "Administration", WalletCards];
const mobilePrimaryOrder = ["/dashboard", "/generator", "/meal-builder", "/calendar", "/profile"] as const;
const betaNavigationItems = new Set(["/meal-builder", "/glucose-trends"]);

export function AppShell({ pathname, email, financeAdmin = false, access, onAdministratorPlanToggle, children }: { pathname: string; email: string; financeAdmin?: boolean; access?: FriendAccessStatus; onAdministratorPlanToggle?: () => Promise<void>; children: React.ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [planChanging, setPlanChanging] = useState(false);
  const [bottomNavigationScroll, setBottomNavigationScroll] = useState({ back: false, forward: false });
  const bottomNavigationRef = useRef<HTMLElement>(null);
  useEffect(() => { setMenuOpen(false); }, [pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);
  useEffect(() => {
    const navigation = bottomNavigationRef.current;
    const activeLink = navigation?.querySelector<HTMLElement>('.nav-link[aria-current="page"]');
    if (!navigation || !activeLink) return;
    const frame = window.requestAnimationFrame(() => {
      const centeredLeft = activeLink.offsetLeft - (navigation.clientWidth - activeLink.offsetWidth) / 2;
      navigation.scrollTo({ left: Math.max(0, centeredLeft), behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [pathname]);
  useEffect(() => {
    const navigation = bottomNavigationRef.current;
    if (!navigation) return;
    const syncScrollControls = () => {
      const back = navigation.scrollLeft > 2;
      const forward = navigation.scrollLeft + navigation.clientWidth < navigation.scrollWidth - 2;
      setBottomNavigationScroll((current) => current.back === back && current.forward === forward ? current : { back, forward });
    };
    const frame = window.requestAnimationFrame(syncScrollControls);
    navigation.addEventListener("scroll", syncScrollControls, { passive: true });
    window.addEventListener("resize", syncScrollControls);
    return () => {
      window.cancelAnimationFrame(frame);
      navigation.removeEventListener("scroll", syncScrollControls);
      window.removeEventListener("resize", syncScrollControls);
    };
  }, [access?.fullAccess, financeAdmin, pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const previousOverflow = document.body.style.overflow;
    const mobileLayout = window.matchMedia("(max-width: 820px)");
    const syncPageScroll = () => {
      document.body.style.overflow = mobileLayout.matches ? "hidden" : previousOverflow;
    };
    syncPageScroll();
    mobileLayout.addEventListener("change", syncPageScroll);
    return () => {
      mobileLayout.removeEventListener("change", syncPageScroll);
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);
  async function signOut() {
    await supabase.auth.signOut();
    navigate("/login", true);
  }
  async function changeAdministratorPlan() {
    if (!onAdministratorPlanToggle || planChanging) return;
    setPlanChanging(true);
    try {
      await onAdministratorPlanToggle();
    } finally {
      setPlanChanging(false);
    }
  }
  function moveBottomNavigation() {
    const navigation = bottomNavigationRef.current;
    if (!navigation) return;
    const direction = bottomNavigationScroll.forward ? 1 : -1;
    navigation.scrollBy({ left: direction * navigation.clientWidth * 0.8, behavior: "smooth" });
  }

  const showUpgrade = access?.fullAccess === false;
  const upgradeLink: NavItem = ["/pricing", "Upgrade", WalletCards];
  const accountLinks: readonly NavItem[] = showUpgrade
    ? [upgradeLink, settingsLink, supportLink]
    : [settingsLink, supportLink];
  const mobilePrimary = [
    ...mobilePrimaryOrder.flatMap((href) => primary.filter((item) => item[0] === href)),
    ...primary.filter((item) => !mobilePrimaryOrder.includes(item[0] as (typeof mobilePrimaryOrder)[number])),
  ];
  const mobileNavigation: readonly NavItem[] = [
    ...mobilePrimary,
    ...tracking,
    ...resources,
    ...accountLinks,
    ...(financeAdmin ? [administrationLink] : []),
  ];

  const links = (items: readonly NavItem[], options: { showBetaTags?: boolean } = {}) => items.map(([href, label, Icon]) => (
    <AppLink key={href} href={href} onClick={() => setMenuOpen(false)} className={`nav-link ${href === "/pricing" ? "upgrade" : ""} ${pathname === href ? "active" : ""}`.trim()} aria-current={pathname === href ? "page" : undefined}>
      <Icon size={18} aria-hidden="true" /><span className="nav-label">{label}</span>{options.showBetaTags && betaNavigationItems.has(href) ? <span className="nav-beta-badge">Beta</span> : null}
    </AppLink>
  ));

  return (
    <div className="app-frame">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <header className="topbar">
        <div className="brand-with-status"><AppLink href="/dashboard" className="brand-lockup compact" aria-label="Precise Plate dashboard"><BrandLogo /></AppLink><BetaBadge /><AppLink href="/install" className="button quiet topbar-install desktop-only"><MonitorDown size={17} />Install app</AppLink><AppLink href="/install" className="icon-button topbar-install mobile-only" aria-label="Install app" title="Install app"><MonitorDown size={18} /></AppLink></div>
        <div className="topbar-plan">
          <PlanStatusBadge access={access} administrator={financeAdmin} busy={planChanging} onAdministratorToggle={() => void changeAdministratorPlan()} />
        </div>
        <div className="topbar-actions">
          <span className="user-email">{email}</span>
          {showUpgrade ? <AppLink href="/pricing" className="button primary topbar-upgrade"><WalletCards size={17} />Upgrade</AppLink> : null}
          <ShareButton label="Share Precise Plate" className="button quiet desktop-only" />
          <ShareButton label="" title="Share Precise Plate" className="icon-button mobile-only" />
          <button className="icon-button mobile-only" onClick={() => setMenuOpen((value) => !value)} aria-label={menuOpen ? "Close navigation" : "Open navigation"} aria-controls="app-navigation" aria-expanded={menuOpen}>{menuOpen ? <X /> : <Menu />}</button>
          <button className="button quiet desktop-only" onClick={signOut}><LogOut size={17} />Sign out</button>
        </div>
      </header>
      <div className="app-grid">
        {menuOpen ? <button className="sidebar-backdrop mobile-only" aria-label="Close navigation" onClick={() => setMenuOpen(false)} /> : null}
        <aside id="app-navigation" className={`sidebar ${menuOpen ? "open" : ""}`}>
          <nav aria-label="Main navigation">{links(primary, { showBetaTags: true })}</nav>
          <div className="nav-divider" />
          <nav aria-label="Health tracking navigation">{links(tracking, { showBetaTags: true })}</nav>
          <div className="nav-divider" />
          <nav aria-label="Learning and installation navigation">{links(resources)}</nav>
          <div className="nav-divider" />
          <nav aria-label="Account navigation">{links(accountLinks)}</nav>
          {financeAdmin ? <><div className="nav-divider" /><nav aria-label="Owner administration">{links([administrationLink])}</nav></> : null}
          <button className="nav-link sign-out mobile-only" onClick={signOut}><LogOut size={18} />Sign out</button>
        </aside>
        <main className="content" id="main-content" tabIndex={-1}>{children}<LegalFooter /></main>
      </div>
      <div className="bottom-navigation-shell">
        <nav ref={bottomNavigationRef} className="bottom-nav" aria-label="All app pages">{links(mobileNavigation)}</nav>
        {bottomNavigationScroll.forward || bottomNavigationScroll.back ? <button className="bottom-nav-scroll" type="button" onClick={moveBottomNavigation} aria-label={bottomNavigationScroll.forward ? "Show more pages" : "Show earlier pages"} title={bottomNavigationScroll.forward ? "More pages" : "Earlier pages"}>{bottomNavigationScroll.forward ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}</button> : <span className="bottom-nav-scroll-spacer" />}
      </div>
    </div>
  );
}

export function BrandLogo() {
  return <img className="brand-logo" src="/brand/precise-plate-logo-compact.png" alt="Precise Plate" />;
}

export function BetaBadge({ className = "" }: { className?: string }) {
  return <span className={`beta-badge ${className}`.trim()}>Beta</span>;
}

export function FullBrandLogo({ className = "" }: { className?: string }) {
  return <div className={`full-brand-logo ${className}`.trim()}>
    <div className="full-brand-artwork">
      <img src="/brand/precise-plate-launch.png" alt="Precise Plate. What to eat?" />
    </div>
    <BetaBadge className="full-brand-beta" />
    <p className="full-brand-tagline">Science-backed meal precision.</p>
  </div>;
}

export function LaunchScreen({ label = "Opening Precise Plate..." }: { label?: string }) {
  return <main className="launch-screen" role="status" aria-live="polite">
    <FullBrandLogo className="launch-brand-logo" />
    <div className="launch-status"><span className="spinner" aria-hidden="true" /><span>{label}</span></div>
  </main>;
}

export function PageHeader({ eyebrow, title, description, action }: { eyebrow?: string; title: string; description?: string; action?: React.ReactNode }) {
  return <header className="page-header"><div>{eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}<h1>{title}</h1>{description ? <p>{description}</p> : null}</div>{action ? <div>{action}</div> : null}</header>;
}

export function LoadingPanel({ label = "Loading..." }: { label?: string }) {
  return <div className="panel loading-panel"><span className="spinner" />{label}</div>;
}

export function EmptyState({ title, body, action }: { title: string; body: string; action?: React.ReactNode }) {
  return <section className="panel empty-state"><h2>{title}</h2><p>{body}</p>{action}</section>;
}
